import { ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  Button,
  Input,
  Pagination,
  Select,
  SelectItem,
  Spinner,
  Tooltip,
} from '@heroui/react';
import { CalendarDate } from '@internationalized/date';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AbuseReportModals,
  AbuseReportTable,
  getUserUsername,
  groupReportsByCard,
  OpportunityModalState,
  ReportGroup,
  ViewModalState,
} from '../../features/admin/AbuseReportHelpers';
import { getReadyRosterCard, getReportsByUser } from '../../services/api';
import { formatCardType } from '../../utils/formatUtils';

const AbuseReport: React.FC = () => {
  const [reportsGrouped, setReportsGrouped] = useState<ReportGroup[]>([]);
  const [searchReporter, setSearchReporter] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState<ReportGroup | null>(null);
  const [viewModal, setViewModal] = useState<ViewModalState>({
    isOpen: false,
    type: null,
    cardId: null,
    cardData: null,
    confirmPublishPopup: false,
    confirmContactPopup: false,
    showReportPopup: false,
  });
  const [opportunityModal, setOpportunityModal] = useState<OpportunityModalState>({
    isOpen: false,
    cardId: null,
  });
  const pageSize = 10;
  const { t } = useTranslation('abusereport');
  const typeOptions = ['All Types', 'MARKETPLACECARD', 'PROFILE', 'POST'];

  const [calendarMonth, setCalendarMonth] = useState<CalendarDate>(() => {
    const today = new Date();
    return new CalendarDate(today.getFullYear(), today.getMonth() + 1, today.getDate());
  });

  const handleViewReports = (group: ReportGroup, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedGroup(group);
  };

  const handleViewCard = async (group: ReportGroup, event: React.MouseEvent) => {
    event.stopPropagation();

    const actualCardId = group.cardId;
    const firstReport = group.reports[0];

    if (group.cardType === 'POST') {
      navigate(`/view-post/${actualCardId}`);
    } else if (group.cardType === 'PROFILE') {
      navigate(`/view-profile?profileId=${actualCardId}`);
    } else if (group.cardType === 'MARKETPLACECARD') {
      if (firstReport.marketplaceCardResponseDto?.opportunityCardResponse != null) {
        setOpportunityModal({
          isOpen: true,
          cardId: actualCardId,
        });
      } else if (firstReport.marketplaceCardResponseDto?.readyRosterCard != null) {
        try {
          setCardLoading(true);
          const cardData = await getReadyRosterCard(actualCardId);
          console.log('Loaded ready roster card data:', cardData);

          if (!cardData || (typeof cardData === 'string' && cardData.trim() === '')) {
            console.error('Invalid card data received from API');
            alert('Failed to load card data. Please try again.');
            return;
          }

          setViewModal({
            isOpen: true,
            type: 'READYROSTERCARD',
            cardId: actualCardId,
            cardData: cardData,
            confirmPublishPopup: false,
            confirmContactPopup: false,
            showReportPopup: false,
          });
        } catch (error) {
          console.error('Error loading ready roster card:', error);
          alert('Failed to load ready roster card. Please check the console for details.');
        } finally {
          setCardLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    const fetchReportsData = async () => {
      setLoading(true);
      try {
        const username = getUserUsername();
        if (!username) throw new Error('User not authenticated');

        const response = await getReportsByUser(username);
        let filteredReports = response;

        if (searchReporter.trim()) {
          filteredReports = filteredReports.filter((report) =>
            `${report.reportedBy.firstName || ''} ${report.reportedBy.lastName || ''}`
              .toLowerCase()
              .includes(searchReporter.trim().toLowerCase())
          );
        }

        if (selectedType && selectedType !== 'All Types') {
          filteredReports = filteredReports.filter((report) => {
            if (selectedType === 'MARKETPLACECARD') {
              return report.marketplaceCardResponseDto?.id;
            } else if (selectedType === 'POST') {
              return report.postResponseDto?.id;
            } else if (selectedType === 'PROFILE') {
              return report.type === 'PROFILE';
            }
            return false;
          });
        }

        const grouped = groupReportsByCard(filteredReports);
        const start = page * pageSize;
        const paginatedGroups = grouped.slice(start, start + pageSize);

        setReportsGrouped(paginatedGroups);
        setTotalRecords(grouped.length);
        setTotalPages(Math.ceil(grouped.length / pageSize));
      } catch (err) {
        console.error('Error fetching reports:', err);
        setReportsGrouped([]);
        setTotalRecords(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchReportsData();
  }, [searchReporter, selectedType, page]);

  const resetFilters = () => {
    setSearchReporter('');
    setSelectedType('');
    setPage(0);
  };

  const closeReportModal = () => setSelectedGroup(null);

  const closeViewModal = () => {
    setViewModal({
      isOpen: false,
      type: null,
      cardId: null,
      cardData: null,
      confirmPublishPopup: false,
      confirmContactPopup: false,
      showReportPopup: false,
    });
  };

  const closeOpportunityModal = () => {
    setOpportunityModal({
      isOpen: false,
      cardId: null,
    });
  };

  return (
    <div className="w-full h-screen flex flex-col">
      <div className="sticky top-0 z-10 border-b bg-white border-gray-200 p-2 flex-shrink-0">
        <div className="max-w-full mx-auto">
          <div className="flex items-center gap-3 flex-wrap bg-white">
            <Input
              placeholder={t('searchReporterPlaceholder')}
              value={searchReporter}
              onChange={(e) => setSearchReporter(e.target.value)}
              className="w-64 min-w-64"
              size="sm"
            />
            <Select
              placeholder="Select Type"
              selectedKeys={selectedType ? [selectedType] : []}
              onSelectionChange={(keys) =>
                setSelectedType((Array.from(keys)[0] as string) || '')
              }
              className="w-48 min-w-48 bg-white"
              size="sm"
            >
              {typeOptions.map((type) => (
                <SelectItem key={type}>{formatCardType(type)}</SelectItem>
              ))}
            </Select>
            <Tooltip content={t('resetFiltersTooltip')}>
              <Button
                isIconOnly
                variant="light"
                onClick={resetFilters}
                className="h-10 w-10 text-gray-600 hover:text-gray-800"
                aria-label="Reset filters"
              >
                <ArrowPathIcon className="w-5 h-5" />
              </Button>
            </Tooltip>
            {(loading || cardLoading) && (
              <div className="flex items-center ml-2">
                <Spinner size="sm" color="primary" />
                <span className="ml-2 text-sm text-gray-600">
                  {cardLoading ? 'Loading card...' : 'Loading...'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1">
        <div className="p-3 max-w-full mx-auto">
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <AbuseReportTable
              reportsGrouped={reportsGrouped}
              loading={loading}
              cardLoading={cardLoading}
              onViewReports={handleViewReports}
              onViewCard={handleViewCard}
              onRowClick={setSelectedGroup}
            />
          </div>

          {!loading && (reportsGrouped.length > 0 || totalPages > 1) && (
            <div className="flex justify-end items-center mt-6 gap-4">
              <div className="flex flex-col items-end gap-2">
                <div className="text-sm text-gray-600">
                  {t('showingRecords', {
                    start: page * pageSize + 1,
                    end: Math.min((page + 1) * pageSize, totalRecords),
                    total: totalRecords,
                  })}
                </div>
                <Pagination
                  total={totalPages}
                  page={page + 1}
                  onChange={(newPage) => setPage(newPage - 1)}
                  showControls
                  color="primary"
                  className="text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <AbuseReportModals
        selectedGroup={selectedGroup}
        viewModal={viewModal}
        opportunityModal={opportunityModal}
        calendarMonth={calendarMonth}
        setCalendarMonth={setCalendarMonth}
        onCloseReportModal={closeReportModal}
        onCloseViewModal={closeViewModal}
        onCloseOpportunityModal={closeOpportunityModal}
        setViewModal={setViewModal}
      />
    </div>
  );
};

export default AbuseReport;